import type { MembershipSummary } from '@nolens/contracts';
import { isStaff } from '@nolens/domain';
import { Button, Card, EmptyState, ErrorState, LoadingState } from '@nolens/ui';
import { Link, Navigate } from 'react-router';
import { NolensMark } from '../app/Brand';
import { ApiError } from '../lib/api';
import { useMe } from '../lib/queries';
import { signOut } from '../lib/session';

export function surfacePath(m: MembershipSummary): string {
  return isStaff(m.roles) ? `/c/${m.organizationId}` : `/p/${m.organizationId}`;
}

/** Decide para onde ir depois do login: uma organização → entra; várias → escolhe. */
export function HomeRedirect() {
  const me = useMe();

  if (me.isPending) return <LoadingState />;
  if (me.isError) {
    return (
      <ErrorState
        onRetry={() => void me.refetch()}
        requestId={me.error instanceof ApiError ? me.error.requestId : undefined}
      />
    );
  }

  const memberships = me.data.memberships;
  if (memberships.length === 1) return <Navigate to={surfacePath(memberships[0]!)} replace />;

  if (memberships.length === 0) {
    return (
      <EmptyState
        title="Ainda não tem acesso a nenhuma clínica"
        description="O acesso é criado pela sua clínica através de um convite. Se já recebeu um convite, abra o link no email. Caso contrário, contacte a clínica."
        action={
          <Button variant="secondary" onClick={() => void signOut()}>
            Terminar sessão
          </Button>
        }
      />
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-12">
      <NolensMark />
      <h1 className="mt-8 text-2xl font-semibold tracking-tight">Escolha a clínica</h1>
      <ul className="mt-6 flex flex-col gap-3">
        {memberships.map((m) => (
          <li key={m.id}>
            <Link to={surfacePath(m)}>
              <Card className="flex items-center gap-3 p-4 transition hover:border-text-3">
                <span className="size-3 rounded-full" style={{ background: m.branding.primaryColor }} aria-hidden />
                <span className="font-medium">{m.branding.displayName}</span>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
